import { useRef, useState, type CSSProperties, type ComponentProps, type MouseEvent } from 'react'

import { cn } from '@/lib/utils'

export type CardVariant = 'default' | 'primary'

type VariantPreset = {
  accentColor: string
  surfaceBackground: string
  idleBorder: string
  hoverBorderColor: string
  radiusPx: number
  borderWidthPx: number
  innerGlowPx: number
  maxTiltDeg: number
  glowIntensity: number
}

/**
 * `primary` — high-emphasis action cards: tilt, full glow, Azzurro Mare border.
 * `default` — list row cards: no tilt, subtler glow, Blu Arca border on hover.
 */
const VARIANT_PRESETS: Record<CardVariant, VariantPreset> = {
  primary: {
    accentColor: 'var(--arca-color-accent)',
    surfaceBackground: 'var(--card)',
    idleBorder: 'var(--border)',
    hoverBorderColor: 'var(--arca-color-accent)',
    radiusPx: 8,
    borderWidthPx: 1,
    innerGlowPx: 650,
    maxTiltDeg: 1.5,
    glowIntensity: 1,
  },
  default: {
    accentColor: 'var(--arca-color-primary)',
    surfaceBackground: 'var(--card)',
    idleBorder: 'var(--border)',
    hoverBorderColor: 'var(--arca-color-primary)',
    radiusPx: 8,
    borderWidthPx: 1,
    innerGlowPx: 560,
    maxTiltDeg: 0,
    glowIntensity: 0.5,
  },
}

export type HoverGlowCardProps = Omit<ComponentProps<'div'>, 'ref'> & {
  /**
   * Visual and interaction preset. `primary` = high-emphasis cards (tilt, full glow,
   * accent border). `default` = list row cards (no tilt, subtler glow, primary border).
   * Individual props override the preset. Defaults to `primary`.
   */
  variant?: CardVariant
  /** Classes applied to the inner surface (padding, layout, typography). */
  innerClassName?: string
  /** Inline style for the inner surface. */
  innerStyle?: CSSProperties
  /** Any CSS color for the inner wash. Alpha steps are derived via `color-mix`. */
  accentColor?: string
  /** CSS color for the surface behind children. */
  surfaceBackground?: string
  /** CSS color for the idle border. */
  idleBorder?: string
  /** CSS color for the border on hover. Defaults to the variant preset. */
  hoverBorderColor?: string
  /** Outer corner radius in px. Inner radius is derived from this minus `borderWidthPx`. */
  radiusPx?: number
  /** Border thickness in px. */
  borderWidthPx?: number
  /** Inner wash diameter in px. Larger = softer. */
  innerGlowPx?: number
  /** Maximum 3-D tilt in degrees. Set to 0 to disable. */
  maxTiltDeg?: number
  /** Multiplier for glow opacity. 1 = full intensity, lower = more subtle. */
  glowIntensity?: number
  /**
   * When true, all hover effects (glow, border tint, tilt, scale) are suppressed
   * regardless of pointer position. Use when an overlay or popover is open and
   * the card should appear inert.
   */
  frozen?: boolean
  /**
   * When true, the card stays locked in its hovered visual state even when the
   * pointer leaves. Use for the card that owns an open confirmation popover so
   * it retains visual hierarchy while the user reads the popover.
   */
  lockedHover?: boolean
}

/**
 * Card surface with a flat accent border on hover and a subtle pointer-tracked
 * inner wash. Use the `variant` prop to pick the platform preset, then override
 * individual props as needed.
 *
 * Forwards arbitrary div props on the outer wrapper, so `role`, `tabIndex`,
 * `aria-*`, `onClick`, etc. work as expected when the card is used as a
 * clickable action.
 */
export function HoverGlowCard({
  children,
  className,
  style,
  innerClassName,
  innerStyle,
  variant = 'primary',
  accentColor,
  surfaceBackground,
  idleBorder,
  hoverBorderColor,
  radiusPx,
  borderWidthPx,
  innerGlowPx,
  maxTiltDeg,
  glowIntensity,
  frozen = false,
  lockedHover = false,
  onMouseMove,
  onMouseEnter,
  onMouseLeave,
  ...rest
}: HoverGlowCardProps) {
  const preset = VARIANT_PRESETS[variant]
  const resolvedAccent = accentColor ?? preset.accentColor
  const resolvedSurface = surfaceBackground ?? preset.surfaceBackground
  const resolvedIdleBorder = idleBorder ?? preset.idleBorder
  const resolvedHoverBorder = hoverBorderColor ?? preset.hoverBorderColor
  const resolvedRadius = radiusPx ?? preset.radiusPx
  const resolvedBorderWidth = borderWidthPx ?? preset.borderWidthPx
  const resolvedInnerGlow = innerGlowPx ?? preset.innerGlowPx
  const resolvedMaxTilt = maxTiltDeg ?? preset.maxTiltDeg
  const resolvedIntensity = glowIntensity ?? preset.glowIntensity
  const rootRef = useRef<HTMLDivElement>(null)
  const [pointer, setPointer] = useState<{ x: number; y: number; w: number; h: number } | null>(
    null,
  )

  const innerRadius = Math.max(0, resolvedRadius - resolvedBorderWidth)
  const isHovering = lockedHover || (!frozen && pointer !== null)

  const updatePointer = (event: MouseEvent<HTMLDivElement>) => {
    if (frozen) return
    const rect = rootRef.current?.getBoundingClientRect()
    if (!rect) return
    setPointer({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      w: rect.width,
      h: rect.height,
    })
  }

  const activePointer =
    pointer ??
    (lockedHover && rootRef.current
      ? (() => {
          const r = rootRef.current!.getBoundingClientRect()
          return { x: r.width / 2, y: r.height / 2, w: r.width, h: r.height }
        })()
      : null)

  const tiltY =
    isHovering && activePointer
      ? ((activePointer.x / activePointer.w) * 2 - 1) * resolvedMaxTilt
      : 0
  const tiltX =
    isHovering && activePointer
      ? -((activePointer.y / activePointer.h) * 2 - 1) * resolvedMaxTilt
      : 0

  const borderColor = isHovering
    ? `color-mix(in srgb, ${resolvedHoverBorder} 60%, transparent)`
    : resolvedIdleBorder

  const cx = activePointer ? activePointer.x - resolvedBorderWidth : 0
  const cy = activePointer ? activePointer.y - resolvedBorderWidth : 0

  const innerWashPeak = Math.round(10 * resolvedIntensity)

  const innerWashBackground = isHovering
    ? `radial-gradient(${resolvedInnerGlow}px circle at ${cx}px ${cy}px, color-mix(in srgb, ${resolvedAccent} ${innerWashPeak}%, ${resolvedSurface}) 0%, transparent 60%)`
    : 'transparent'

  return (
    <div
      {...rest}
      ref={rootRef}
      onMouseEnter={(event) => {
        updatePointer(event)
        onMouseEnter?.(event)
      }}
      onMouseMove={(event) => {
        updatePointer(event)
        onMouseMove?.(event)
      }}
      onMouseLeave={(event) => {
        if (!lockedHover) setPointer(null)
        onMouseLeave?.(event)
      }}
      className={className}
      style={{
        perspective: '900px',
        ...style,
      }}
    >
      <div
        style={{
          position: 'relative',
          height: '100%',
          borderRadius: `${resolvedRadius}px`,
          padding: resolvedBorderWidth,
          backgroundColor: borderColor,
          transform: `perspective(900px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale(${isHovering ? 1.02 : 1})`,
          boxShadow: isHovering
            ? `0 8px 24px color-mix(in srgb, ${resolvedAccent} 12%, transparent), 0 2px 6px rgb(0 0 0 / 8%)`
            : '0 1px 3px rgb(0 0 0 / 4%)',
          transition: isHovering
            ? 'transform 80ms ease-out, background-color 80ms ease-out, box-shadow 150ms ease'
            : 'transform 300ms ease, background-color 300ms ease, box-shadow 300ms ease',
          willChange: 'transform',
        }}
      >
        <div
          className="relative h-full overflow-hidden"
          style={{
            background: resolvedSurface,
            borderRadius: `${innerRadius}px`,
          }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              zIndex: 0,
              borderRadius: `${innerRadius}px`,
              background: innerWashBackground,
              boxShadow: isHovering
                ? `inset 0 0 1px 1px color-mix(in srgb, ${resolvedAccent} ${Math.round(8 * resolvedIntensity)}%, ${resolvedSurface})`
                : 'none',
              opacity: isHovering ? 1 : 0,
              transition: 'opacity 120ms ease',
            }}
          />
          <div
            className={cn('relative h-full', innerClassName)}
            style={{ zIndex: 1, ...innerStyle }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HoverGlowCard
